import { FormControl, FormGroup } from '@angular/forms';
import { DateTime } from 'luxon';

import { IdName } from '@api/models/idname';
import { AverageReachItemsPerHour, GenderEnum } from './reach/reach.models';
import { Segment, Screen, StatisticsEngagementMediaFiles, Channel, Customer, TimeframeEnum } from '@api/index';
import { MediaStats } from '@app/shared/models/media-stats';
import { TrackingEnum } from '@app/shared/models/enums/deviceTracking.enum';
import { DropdownOption } from '@shared/form/form-combobox/form-combobox.model';

export interface ScreenFilterConfig {
  label?: string;
  options: DropdownOption[];
  onSearch: (term: string) => void;
  onLoadMore: (params: { pageNumber: number }) => void;
  params: { totalCount: number; pageNumber: number; pageSize: number };
}

export interface DashboardFilterConfig {
  channels: Channel[];
  timeframeTypes: IdName[];
  customers?: Customer[];
  isAdmin?: boolean;
  screenFilter?: ScreenFilterConfig;
}

export interface StatisticsInitData {
  segments?: Segment[];
  screens: Screen[];
  targetGroups?: TargetGroup[];
  engagementMediaFiles?: StatisticsEngagementMediaFiles[];
}

export interface ChartWeekday {
  dateTime: DateTime;
  utcString: string;
  label: string;
}

export interface PeopleCounterStatisticsResponse {
  averageTimeReportPerHour: AverageTimeInChannelPerHour[];
  metricBoxLanes: MetricBoxLanes;
  topInboundForPeriod: IdName;
  topOutBoundForPeriod: IdName;
}

export interface MetricBoxLanes {
  daily: MetricBoxDaily;
  weekly: MetricBoxWeekly;
  topInboundForPeriod: IdName;
  topOutBoundForPeriod: IdName;
}

export interface MetricBoxDaily {
  totalToday: number;
  totalLastWeekCurrentDay: number;
  todayVsLastWeekDayPercentage: number;
  genderDistribution: Gender;
  lastWeekCurrentDayTopGroup?: TopGroup;
  todayTopGroup?: TopGroup;
  currentPeopleInChannel?: number;
}

export interface MetricBoxWeekly {
  totalCurrentWeek: number;
  totalLastWeek: number;
  todayVsLastWeekPercentage: number;
  genderDistribution: Gender;
  currentWeekTopGroup?: TopGroup;
  previousWeekTopGroup?: TopGroup;
}

export interface WeatherPerHourResponse {
  hour: string;
  temperature: number;
  weather: string;
  precipitation?: number;
  windSpeed?: number;
  windDegree?: number;
}

export interface Gender {
  Male?: number;
  Female?: number;
}

export interface TopGroup {
  gender: number;
  ageSpan: string;
}

export interface TopScreen {
  name: string;
  isSensor: boolean;
}

export interface GenderDistributionThisWeek {
  percentageOfMen: number;
  percentageOfWomen: number;
}

export interface AgeDistributionThisWeek {
  ageRangeDistribution: Array<AgeRangeDistribution>;
  total: number;
}

export interface AgeRangeDistribution {
  ageRange: string;
  totalMen: number;
  totalWomen: number;
  total: number;
}

export interface VisitorsPerHourWeekly {
  currentWeek: Array<HourlySeparatedTarget>;
  lastWeek: Array<HourlySeparatedTarget>;
}

export interface HourlySeparatedTarget {
  hour: number;
  totalMen: number;
  totalWomen: number;
}


export interface AverageTimeInChannelPerHour {
  womenAverageTimeInChannel: string;
  womenAverageTimeInChannelMinutes: number;
  hour: number;
  menAverageTimeInChannel: string;
  menAverageTimeInChannelMinutes: number;
}

export interface PeopleCountFormGroup {
  customer: FormControl<Customer>;
  channels: FormControl<Channel[]>;
  screenFilter?: FormControl<number[]>;
  timeframe: FormControl<number>;
  start: FormControl<DateTime>;
  end: FormControl<DateTime>;
  rangeCompare: FormGroup<{ start: FormControl<DateTime>; end: FormControl<DateTime> }>;
  tracking?: FormControl<TrackingEnum[]>;
}

export interface VisitorsResponse {
  visitors: VisitorsPerHour[];
  compareVisitors: VisitorsPerHour[];
}

export interface VisitorsPerHour {
  hour: number;
  totalVisitors: number;
  averageVisitors: number;
}

export interface VisitorsPerHourFormated {
  hours: string[];
  totalVisitors: number[];
}

export interface ReachDataSection<T> {
  data: T;
  compareData: T | null;
}

export interface KpiBoxFormated {
  label: string;
  value: number;
}

export interface KpiBoxes {
  people: KpiBoxFormated[];
  vehicles: KpiBoxFormated[];
}

export interface AgeAndGenderDistributionResponse {
  averagePeoplePerHour: AverageReachItemsPerHour[];
  targetGroups: TargetGroup[];
  totalMen: number;
  totalOts: number;
  totalWomen: number;
}

export interface TargetGroup {
  targetGroup: number;
  count: number;
}

export interface OverviewData<TBar, TPie> {
  distribution: TBar;
  distributionCompare?: TBar;
  totals: TPie;
  totalsCompare?: TPie;
  timeFrame: TimeframeEnum;
}

export interface FormatedPeopleDistribution {
  hours: string[];
  average: number[];
  menArray: number[];
  womenArray: number[];
}

export interface PeopleTypeData {
  totalMen: number;
  totalWomen: number;
  totalOts: number;
}

export interface AgeDistributionResponse {
  ageRangeDistribution: AgeDistribution[];
  total: number;
}

export interface AgeDistribution {
  ageRange: string;
  totalMen: number;
  totalWomen: number;
  total: number;
}

export interface ExposuresMediaStats {
  bookingId: number;
  mediaKpiData: MediaStats[];
  daysForFilteredPeriod: string[];
  hasAdvertisingPricing: boolean;
}

export interface ExposuresRequest {
  bookingIds: number[];
  clientIds: number[];
  startDate: string;
  endDate: string;
}

export interface ExposuresTotalsResponse {
  bookingId: number;
  mediaNames: string[];
  mediaTotalExposures: ExposuresTotalsDataset[];
  mediaShareOfVoice: ExposuresTotalsDataset[];
  mediaAttentionTime: ExposuresTotalsDataset[];
}

export interface ExposuresAverageResponse {
  bookingId: number;
  dataSets: Dataset[];
  labels: string[];
  mediaFiles: IdName[];
  // Only populated by the exposures-weather-per-day endpoint, which shares this response shape
  // with the exposure/attention-span endpoints.
  weatherPerDay?: ChannelWeatherDto[];
}

export interface ExposuresTotalsDataset {
  mediaId: number;
  value: number;
}

export interface Dataset {
  data: number[];
  mediaId: number;
  gender?: GenderEnum;
  images?: string[];
  label: string
}

export interface ComparisonDataSet {
  value: number;
  mediaId: number;
}

export interface ImpressionsVsBudgetedImpressionsResponse {
  labels?: string[];
  mediaFiles?: IdName[];
  dataSets: ComparisonDataSet[];
  bookingId: number;
}

export interface ExposuresVsBudgetedExposuresResponse {
  labels?: string[];
  mediaFiles?: IdName[];
  dataSets: ComparisonDataSet[];
  bookingId: number;
}

export interface IntegratedExposuresResponse {
  exposures: ExposuresTotalsResponse;
  totalNumberOfExposures: ExposuresTotalsResponse;
  averageExposures: ExposuresAverageResponse;
  attentionTimeAveragePerHour: ExposuresAverageResponse;
  weatherData: ExposuresAverageResponse;
  impressionsVsBudgetedImpressions: ImpressionsVsBudgetedImpressionsResponse | null;
  exposuresVsBudgetedExposures: ExposuresVsBudgetedExposuresResponse | null;
}

export enum ChartColors {
  Primary = '#1f5792',
  PrimaryLight = '#bfd6e4',
  PrimaryDark = '#598fae',
  Secondary = '#fa6200',
  SecondaryLight = '#f4d3c0',
  SecondaryDark = '#e8aa86',
  Tertiary = '#fc4b47',
  TertiaryLight = '#ffaaa5',
  TertiaryDark = '#a72323',
  Quaternary = '#177578',
  QuaternaryLight = '#a8d5d6',
  QuaternaryDark = '#105254',
  Gray = '#bbbbbb',
  GrayLight = '#e8e8e8',
  GrayDark = '#888888',
  White = '#fff',
}

export enum ExposuresResponse {
  exposures = 'exposures',
  weatherData = 'weatherData',
  totalNumberOfExposures = 'totalNumberOfExposures',
  averageExposures = 'averageExposures',
  attentionTimeAveragePerHour = 'attentionTimeAveragePerHour',
  impressionsVsBudgetedImpressions = 'impressionsVsBudgetedImpressions',
  exposuresVsBudgetedExposures = 'exposuresVsBudgetedExposures',
}

export enum WeatherColors {
  Cold = '#7416DA',
  Cool = '#33ADFF',
  Warm = '#FFD30F',
  Hot = '#FF0A3F',
}

export interface BaseStatisticsRequest {
  startDate: string;
  endDate: string;
  customerId: number;
  screenIds: number[];
}

export interface DailyExposuresAndWeatherResponse {
  dailyExposuresAndWeather: DailyExposuresAndWeather[];
  filename: string;
  id: number | null;
  name: string;
}

export interface DailyExposuresAndWeather {
  totalsPerDay: TotalsPerDay;
  channelWeatherDto: ChannelWeatherDto;
}

export interface ChannelWeatherDto {
  temperature: number;
  weather: string;
  date?: string;
  precipitation?: number;
  windSpeed?: number;
  windDegree?: number;
  temperatureMin?: number;
  temperatureMax?: number;
}

export interface TotalsPerDay {
  day: string;
  totals: number;
}
